/**
 * ProviderSignInButtons — the Google + Apple provider sign-in button pair.
 *
 * Cross-feature component: the same button pair is rendered by the Login screen
 * (`feature/login/components/LoginProviderButtons`) and by the Delete Account
 * page (`feature/deleteAccount/components/DeleteAccountLoginSection`), so the
 * markup lives here once and both surfaces consume it. Each caller owns its own
 * layout wrapper (column direction, gap, width cap) and its own copy around the
 * buttons; this component paints ONLY the two buttons and no outer margin.
 *
 * Flutter source of truth: `LoginScreen.googleLoginButton()`
 * (`lib/presentation/screens/login/login_screen.dart:358-398`) and
 * `appleLoginButton()` (`:400-442`) — each button carries its 20x20 brand mark
 * from `assets/images/<provider>.png` (`:378-382` / `:421-425`) followed by the
 * label `'Continue with Google'` (`:388`) / `'Continue with Apple'` (`:431`) at
 * `signInButtonTextStyle`. The web renders both through the shared
 * `ExpauseButton` (`ButtonVariant.secondary`, `ButtonSize.large`), which already
 * supplies Flutter's hairline `AppColors.exclusive` border + radius (`:367-368`)
 * and the 16px vertical padding (`:371`). Both buttons are full-bleed (`w-full`)
 * within the caller's column, mirroring Flutter's
 * `Column(crossAxisAlignment: stretch)` (`:344-345`).
 *
 * The screen-level parity ledger (which providers are shown, how taps are wired,
 * the documented web deviations) stays with each caller — see
 * `LoginProviderButtons`' file header for the login screen's version.
 */
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import {
  ExpauseButton,
  ButtonVariant,
  ButtonSize,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * Per-surface `data-qa-id` values. The two surfaces are located by different
 * ids (`login-*` vs `delete-account-*`) and both are referenced by name from
 * `workflow/ui_test_plans/**`, so each caller passes its own ids verbatim —
 * they are never derived from a prefix here.
 */
export interface ProviderSignInButtonsQaIds {
  /** `data-qa-id` for the Google button on this surface. */
  googleId: string;
  /** `data-qa-id` for the Apple button on this surface. */
  appleId: string;
}

interface ProviderSignInButtonsProps {
  /** Disables both buttons while a sign-in flow is in progress. */
  isLoggingIn: boolean;
  onLoginWithGoogle: () => void;
  onLoginWithApple: () => void;
  /** Per-surface QA ids — the two surfaces are located by different ids. */
  qa: ProviderSignInButtonsQaIds;
}

export const ProviderSignInButtons = ({
  isLoggingIn,
  onLoginWithGoogle,
  onLoginWithApple,
  qa,
}: ProviderSignInButtonsProps) => {
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  return (
    <>
      {/* Google Sign In Button — full-width secondary ExpauseButton. The Google
          mark is rendered as a full-color <img> child from the public asset (not
          the masking `icon` prop) so the four brand colors survive. */}
      <ExpauseButton
        {...qaAttr({ id: qa.googleId })}
        variant={ButtonVariant.secondary}
        size={ButtonSize.large}
        onClick={onLoginWithGoogle}
        disabled={isLoggingIn}
        className="w-full"
      >
        <img
          src="/assets/images/google.png"
          alt=""
          aria-hidden="true"
          style={{
            width: responsive.icon.medium,
            height: responsive.icon.medium,
            marginRight: responsive.spacing.sm,
          }}
        />
        <span>{l10n.auth.continueWithGoogle}</span>
      </ExpauseButton>

      {/* Apple Sign In Button — full-width secondary ExpauseButton. Apple's mark
          is monochrome, so routing the asset through the masking `icon` prop is
          fine. */}
      <ExpauseButton
        {...qaAttr({ id: qa.appleId })}
        variant={ButtonVariant.secondary}
        size={ButtonSize.large}
        onClick={onLoginWithApple}
        disabled={isLoggingIn}
        className="w-full"
      >
        <img
          src="/assets/images/apple.png"
          alt=""
          aria-hidden="true"
          style={{
            width: responsive.icon.medium,
            height: responsive.icon.medium,
            marginRight: responsive.spacing.sm,
          }}
        />
        <span>{l10n.auth.continueWithApple}</span>
      </ExpauseButton>
    </>
  );
};
